// Lightweight i18n for the dimming-profile editor, mirroring the commandBus
// i18n approach (module-level dictionaries keyed by locale, no framework).
// When the app adopts a full i18n framework, move these dictionaries into the
// locale JSON files; the call sites stay identical.

import { DaliAddressMode, DayKey } from '../types/dimmingProfile.types'

export type Locale = 'fr' | 'en'

let currentLocale: Locale = 'fr'

export function getLocale(): Locale {
  return currentLocale
}

export function setLocale(locale: Locale): void {
  currentLocale = locale
}

export interface DimmingProfileUiStrings {
  title: string
  timeAxis: string
  levelAxis: string
  addStep: string
  removeStep: string
  activeDays: string
  daliAddress: string
  daliSingleId: string
  daliGroupId: string
  inactive: string
  level: string
  time: string
  snapHint: string
  noSteps: string
  reset: string
  // dali mode labels
  daliMode: Record<DaliAddressMode, string>
  // day labels (short)
  day: Record<DayKey, string>
  // ── 0x20 zenith calendar ──
  calendarTitle: string
  sunrise: string
  sunset: string
  calendarExample: string // "fill with an example" button
  calendarExampleConfirmTitle: string // confirmation modal title
  calendarExampleConfirm: string // confirmation modal message
  calendarExampleApply: string // confirmation modal: replace button
  calendarExampleCancel: string // confirmation modal: cancel button
  zenithAngle: string // column header (°)
  dimmingLevel: string // column header (%)
  addCalendarStep: string
  removeCalendarStep: string
  noCalendarSteps: string
  latitude: string
  longitude: string
  prefersMetaPos: string
  clampsProfiles: string
  clampsProfilesHint: string
  clampsDig: string
  ignoreGnss: string
  zenithHint: string
  // coordinates prefilled from the fleet's linked minigrid
  coordsFromMinigrid: (name: string) => string
  coordsLinkMinigridHint: string
  coordsReadOnlyHint: string
  // intensity-vs-time curve preview
  calendarCurveHint: string
  calendarCurveNoData: string
  // graphical editing of the daily calendar curve
  calendarEditHint: string
  calendarLinkToEdit: string
  // ── direct zenith-axis step editor (mirrors the profile editor) ──
  zenithAxis: string // x-axis caption (°)
  calendarStepsHint: string // hint under the editor
  calendarSunriseEditor: string // sunrise editor title
  calendarSunsetEditor: string // sunset editor title
  // ── sun-position reference cues drawn on the zenith editor ──
  zenithHorizonLabel: string // "Horizon" marker at 90°
  zenithDayLabel: string // band label over θ < 90°
  zenithTwilightLabel: string // band label over 90°–108°
  zenithNightLabel: string // band label over θ > 108°
  zenithSunHint: string // plain-language hint under the chart
  sunRisingHint: string // directional cue (phase="sunrise")
  sunSettingHint: string // directional cue (phase="sunset")
  // ── daily steps (sunrise → sunset) preview ──
  dailyStepsTitle: string
  dailyStepsHint: string
  // ── annual zenith preview ──
  annualZenithTitle: string
  annualElevationLabel: string
  annualIntensityLabel: string
  annualMonthAxis: string
  annualDegAxis: string
  annualNoData: string
  annualMonths: string[] // 12 short month labels, Jan..Dec
}

const STRINGS: Record<Locale, DimmingProfileUiStrings> = {
  fr: {
    title: 'Profil de gradation',
    timeAxis: 'Heure',
    levelAxis: 'Niveau (%)',
    addStep: 'Ajouter un palier',
    removeStep: 'Supprimer',
    activeDays: 'Jours actifs',
    daliAddress: 'Adresse DALI',
    daliSingleId: 'ID individuel (0-63)',
    daliGroupId: 'ID de groupe (0-15)',
    inactive: 'Inactif',
    level: 'Niveau',
    time: 'Heure',
    snapHint: 'Les paliers sont alignés sur la grille de 10 minutes.',
    noSteps: 'Aucun palier. Cliquez sur la courbe pour en ajouter un.',
    reset: 'Réinitialiser',
    daliMode: {
      analog: 'Analogique (0x01)',
      broadcast: 'Diffusion DALI (0xFE)',
      all: 'Tous les appareils (0xFF)',
      single: 'Individuel',
      group: 'Groupe',
    },
    day: {
      mon: 'Lun',
      tue: 'Mar',
      wed: 'Mer',
      thu: 'Jeu',
      fri: 'Ven',
      sat: 'Sam',
      sun: 'Dim',
      holiday: 'Férié',
    },
    calendarTitle: 'Calendrier zénithal (lever / coucher)',
    sunrise: 'Lever du soleil',
    sunset: 'Coucher du soleil',
    calendarExample: 'Exemple',
    calendarExampleConfirmTitle: 'Remplacer les paliers',
    calendarExampleConfirm:
      'Voulez-vous vraiment remplacer les paliers configurés par la configuration exemple ? Les valeurs actuelles seront perdues.',
    calendarExampleApply: 'Remplacer',
    calendarExampleCancel: 'Annuler',
    zenithAngle: 'Angle zénithal (°)',
    dimmingLevel: 'Niveau (%)',
    addCalendarStep: 'Ajouter un palier',
    removeCalendarStep: 'Supprimer',
    noCalendarSteps: 'Aucun palier.',
    latitude: 'Latitude (°)',
    longitude: 'Longitude (°)',
    prefersMetaPos: 'Préférer la position des métadonnées',
    clampsProfiles: 'Borner le profil horaire',
    clampsProfilesHint: 'Le calendrier borne le profil horaire (0x21).',
    clampsDig: 'Borner le DIG',
    ignoreGnss: 'Ignorer le GNSS',
    zenithHint: '90° = horizon ; > 90° = soleil sous l’horizon (crépuscule).',
    coordsFromMinigrid: (name) =>
      `Coordonnées depuis la minigrid « ${name} ».`,
    coordsLinkMinigridHint:
      'Liez une minigrid pour récupérer les coordonnées.',
    coordsReadOnlyHint:
      'Coordonnées issues de la minigrid liée (non modifiables).',
    calendarCurveHint:
      'Aperçu illustratif de l’intensité sur une journée type (équinoxe). Lever ≈ 06:00, coucher ≈ 18:00.',
    calendarCurveNoData:
      'Renseignez les coordonnées (depuis la minigrid) et des paliers pour visualiser la courbe.',
    calendarEditHint:
      'Cliquez pour ajouter un palier, glissez pour l’ajuster, double-clic pour supprimer.',
    calendarLinkToEdit: 'Liez une minigrid pour éditer les paliers.',
    zenithAxis: 'Angle zénithal (°)',
    calendarStepsHint:
      'Cliquez pour ajouter un palier, glissez pour ajuster, double-clic pour supprimer.',
    calendarSunriseEditor: 'Paliers — lever du soleil',
    calendarSunsetEditor: 'Paliers — coucher du soleil',
    zenithHorizonLabel: 'Horizon',
    zenithDayLabel: 'Jour',
    zenithTwilightLabel: 'Crépuscule',
    zenithNightLabel: 'Nuit',
    zenithSunHint:
      'L’angle zénithal indique la position du soleil : 90° = horizon (lever/coucher), au-dessous = soleil dans le ciel (jour), au-dessus = soleil sous l’horizon (crépuscule puis nuit).',
    sunRisingHint: 'Le soleil se lève →',
    sunSettingHint: 'Le soleil se couche →',
    dailyStepsTitle: 'Paliers sur la journée (lever → coucher)',
    dailyStepsHint:
      'Aperçu des paliers de gradation entre le lever et le coucher du soleil (jour type, équinoxe).',
    annualZenithTitle:
      'Zénithal solaire : variation annuelle et quotidienne',
    annualElevationLabel: 'Élévation solaire maximale (zénith)',
    annualIntensityLabel: 'Intensité lumineuse par paliers',
    annualMonthAxis: 'Mois',
    annualDegAxis: 'Degrés (°)',
    annualNoData: 'Latitude requise pour l’aperçu annuel.',
    annualMonths: [
      'Jan',
      'Fév',
      'Mar',
      'Avr',
      'Mai',
      'Juin',
      'Juil',
      'Août',
      'Sep',
      'Oct',
      'Nov',
      'Déc',
    ],
  },
  en: {
    title: 'Dimming profile',
    timeAxis: 'Time',
    levelAxis: 'Level (%)',
    addStep: 'Add step',
    removeStep: 'Remove',
    activeDays: 'Active days',
    daliAddress: 'DALI address',
    daliSingleId: 'Single id (0-63)',
    daliGroupId: 'Group id (0-15)',
    inactive: 'Inactive',
    level: 'Level',
    time: 'Time',
    snapHint: 'Steps snap to the 10-minute grid.',
    noSteps: 'No steps. Click the curve to add one.',
    reset: 'Reset',
    daliMode: {
      analog: 'Analog (0x01)',
      broadcast: 'DALI broadcast (0xFE)',
      all: 'All devices (0xFF)',
      single: 'Single',
      group: 'Group',
    },
    day: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
      holiday: 'Holiday',
    },
    calendarTitle: 'Zenith calendar (sunrise / sunset)',
    sunrise: 'Sunrise',
    sunset: 'Sunset',
    calendarExample: 'Example',
    calendarExampleConfirmTitle: 'Replace the steps',
    calendarExampleConfirm:
      'Replace the configured steps with the example configuration? The current values will be lost.',
    calendarExampleApply: 'Replace',
    calendarExampleCancel: 'Cancel',
    zenithAngle: 'Zenith angle (°)',
    dimmingLevel: 'Level (%)',
    addCalendarStep: 'Add step',
    removeCalendarStep: 'Remove',
    noCalendarSteps: 'No steps.',
    latitude: 'Latitude (°)',
    longitude: 'Longitude (°)',
    prefersMetaPos: 'Prefer metadata position',
    clampsProfiles: 'Clamp time profile',
    clampsProfilesHint: 'The calendar clamps the time profile (0x21).',
    clampsDig: 'Clamp DIG',
    ignoreGnss: 'Ignore GNSS',
    zenithHint: '90° = horizon; > 90° = sun below the horizon (twilight).',
    coordsFromMinigrid: (name) => `Coordinates from minigrid “${name}”.`,
    coordsLinkMinigridHint: 'Link a minigrid to fetch the coordinates.',
    coordsReadOnlyHint: 'Coordinates from the linked minigrid (read-only).',
    calendarCurveHint:
      'Illustrative preview of the intensity over a representative (equinox) day. Sunrise ≈ 06:00, sunset ≈ 18:00.',
    calendarCurveNoData:
      'Enter the coordinates (from the minigrid) and some steps to see the curve.',
    calendarEditHint:
      'Click to add a step, drag to adjust, double-click to remove.',
    calendarLinkToEdit: 'Link a minigrid to edit the steps.',
    zenithAxis: 'Zenith angle (°)',
    calendarStepsHint:
      'Click to add a step, drag to adjust, double-click to remove.',
    calendarSunriseEditor: 'Steps — sunrise',
    calendarSunsetEditor: 'Steps — sunset',
    zenithHorizonLabel: 'Horizon',
    zenithDayLabel: 'Day',
    zenithTwilightLabel: 'Twilight',
    zenithNightLabel: 'Night',
    zenithSunHint:
      'The zenith angle shows the sun’s position: 90° = horizon (sunrise/sunset), below = sun in the sky (day), above = sun under the horizon (twilight then night).',
    sunRisingHint: 'The sun is rising →',
    sunSettingHint: 'The sun is setting →',
    dailyStepsTitle: 'Daily steps (sunrise → sunset)',
    dailyStepsHint:
      'Preview of the dimming steps between sunrise and sunset (representative equinox day).',
    annualZenithTitle: 'Solar zenith: annual & daily variation',
    annualElevationLabel: 'Maximum solar elevation (zenith)',
    annualIntensityLabel: 'Light intensity by steps',
    annualMonthAxis: 'Month',
    annualDegAxis: 'Degrees (°)',
    annualNoData: 'Latitude required for the annual preview.',
    annualMonths: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
  },
}

export function dimmingProfileStrings(locale: Locale): DimmingProfileUiStrings {
  return STRINGS[locale] ?? STRINGS.fr
}
